import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { mercuryApi } from '$api'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { Dayjs } from 'dayjs'
import { AuthModule, PayrollSystem } from '$constants'
import { Calendar, PayRun, ThirdPartyConnectionStatus } from '$models'
import { payrollService } from '$services'
interface PayRunsState {
  payRuns: PayRun[]
  calendars: Calendar[]
}

export interface PayRunsSlice extends PayRunsState {
  /**
   * Resets Payruns Slice state
   */
  resetPayRunsSlice: () => void

  /**
   * Get Pay runs list
   * @param payrollSystem company payroll system
   */
  getPayRuns: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Get Pay Calendars list
   * @param payrollSystem company payroll system
   */
  getCalendars: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Add PayRun
   * @param payRun PayRun to add
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  addPayRun: (
    payrollSystem: PayrollSystem,
    payRun: { paymentDate: Dayjs; calendarId: string },
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Check Xero connection
   * @param authModule Time or Stock
   * @returns
   */
  checkXeroConnection: (
    authModule: AuthModule
  ) => Promise<ThirdPartyConnectionStatus | null>
}

const initialState: PayRunsState = {
  payRuns: [],
  calendars: [],
}

const createPayRunsSlice: StateCreator<
  PayRunsSlice & SharedSlice,
  [],
  [],
  PayRunsSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetPayRunsSlice: () => set(initialState),

  // State API calls
  getPayRuns: async (payrollSystem: PayrollSystem): Promise<void> => {
    return payrollService
      .getPayRuns(payrollSystem)
      .then((result) => {
        set(() => ({
          payRuns: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          payRuns: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve payruns')
      })
  },

  getCalendars: async (payrollSystem: PayrollSystem): Promise<void> => {
    return payrollService
      .getCalendars(payrollSystem)
      .then((result) => {
        set(() => ({
          calendars: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          calendars: [],
        }))

        exceptionMessageHandler(error, 'Failed to retrieve calendars')
      })
  },

  addPayRun: async (
    payrollSystem: PayrollSystem,
    payRun: { paymentDate: Dayjs; calendarId: string },
    successMessage?: string,
    errorMessage?: string
  ) => {
    set(() => ({ isLoading: true }))
    return payrollService
      .addPayRun(payrollSystem, payRun)
      .then((result) => {
        if (!result) {
          logger.error(`${errorMessage} - addedPayrun doesn't exist`)
          enqueueLoadedSnackbar(errorMessage, 'error')
          return
        }

        set((state) => ({
          payrun: result,
          payRuns: [...state.payRuns, result],
        }))
        enqueueLoadedSnackbar(successMessage, 'success')
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  checkXeroConnection: async (
    authModule: AuthModule
  ): Promise<ThirdPartyConnectionStatus | null> => {
    return mercuryApi
      .get<ThirdPartyConnectionStatus>(
        `/connections/xero/status?module=${authModule}`
      )
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to check connection with Xero')
        return null
      })
  },
})

export default createPayRunsSlice
